import { Box } from '@mantine/core';
import { CSSProperties, ReactElement, ReactNode } from 'react';
import { useSearchParams } from 'react-router';
import { TabOption, TimelineTabs } from '@/components/Timeline/TimelineTabs';

const PANEL_STYLE: CSSProperties = {
  flex: 1,
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
  border: '1px solid rgba(0, 0, 0, 0.10)',
  borderRadius: 10,
  background: 'var(--ev-color-warm-white, #fafcf7)',
  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
};

function isValidTab<T extends string>(value: string | null, tabs: TabOption<T>[]): value is T {
  return value !== null && tabs.some((tab) => tab.value === value);
}

/** The bordered hub panel with its tab strip; the active tab lives in `?tab=`. */
export function HubShell<T extends string>({
  tabs,
  defaultTab,
  children,
}: {
  tabs: TabOption<T>[];
  defaultTab: T;
  children: (activeTab: T) => ReactNode;
}): ReactElement {
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const activeTab = isValidTab(tabParam, tabs) ? tabParam : defaultTab;

  const handleTabChange = (value: T): void => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('tab', value);
      return next;
    });
  };

  return (
    <Box style={PANEL_STYLE}>
      <TimelineTabs tabs={tabs} activeTab={activeTab} onTabChange={handleTabChange} variant="panel" />
      {children(activeTab)}
    </Box>
  );
}
